import React, { useEffect, useState } from 'react';
import { useAppShell } from '../app-shell';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Fab, Tabs, Tab } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CommunityIcon from '@mui/icons-material/LocationCityRounded';
import { fetchCommunityAsync, clearError } from '../../actions/community-actions';
import CommunityService from '../../services/community-service';
import EditPageLayout from '../common/EditPageLayout';
import type { AppDispatch, RootState } from '../../store';

interface CommunityData {
  recordID: string;
  name?: string;
  description?: string;
  privacy?: string;
  memberStasherIds?: string[];
  communityVendors?: unknown[];
  vibeImage?: { url: string };
  meta?: { createdAt?: string };
  adminStasherIds?: string[];
  editorStasherIds?: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const CommunityImages = ({ community }: { community: CommunityData }): React.JSX.Element => (
  <Box sx={{ mb: 3 }}>
    {community?.vibeImage && (
      <img
        src={community.vibeImage.url}
        alt="Community vibe"
        style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 8 }}
      />
    )}
  </Box>
);

const CommunityMembers = ({ community }: { community: CommunityData }): React.JSX.Element => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      Members ({community?.memberStasherIds?.length ?? 0})
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Member management coming soon...
    </Typography>
  </Box>
);

const CommunityVendors = ({ community }: { community: CommunityData }): React.JSX.Element => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      Vendors ({community?.communityVendors?.length ?? 0})
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Vendor management coming soon...
    </Typography>
  </Box>
);

const CommunityInfo = ({ community }: { community: CommunityData }): React.JSX.Element => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      Community Info
    </Typography>
    <Typography variant="body2">Privacy: {community?.privacy ?? 'Not specified'}</Typography>
    <Typography variant="body2">
      Created:{' '}
      {community?.meta?.createdAt
        ? new Date(community.meta.createdAt).toLocaleDateString()
        : 'Unknown'}
    </Typography>
  </Box>
);

function TabPanel({ children, value, index, ...other }: TabPanelProps): React.JSX.Element {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`community-tabpanel-${index}`}
      aria-labelledby={`community-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CommunityPage = (): React.JSX.Element => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { setBreadcrumb } = useAppShell();

  const [tabValue, setTabValue] = useState(0);
  const [canEdit, setCanEdit] = useState(false);

  const { currentCommunity, isLoading, error } = useSelector(
    (state: RootState) => state.community,
  );

  const { currentUser } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (id) {
      dispatch(clearError());
      dispatch(fetchCommunityAsync(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const name = (currentCommunity as unknown as { name?: string })?.name;
    setBreadcrumb(name ? ['Communities', name] : ['Community']);
    return () => setBreadcrumb([]);
  }, [setBreadcrumb, currentCommunity]);

  useEffect(() => {
    if (currentCommunity && currentUser) {
      const communityService = new CommunityService();
      const canUserEdit = communityService.canUserEdit(currentCommunity as unknown as CommunityData);
      setCanEdit(canUserEdit);
    } else {
      setCanEdit(false);
    }
  }, [currentCommunity, currentUser]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };

  const handleEdit = (): void => {
    void navigate(`/app/communities/${id}/edit`);
  };

  const getErrorMessage = (): string | null => {
    if (error) return error;
    if (!currentCommunity && !isLoading) return 'Community not found';
    return null;
  };

  return (
    <EditPageLayout
      title={(currentCommunity as unknown as CommunityData | null)?.name ?? 'Community'}
      titleIcon={<CommunityIcon />}
      subtitle={(currentCommunity as unknown as CommunityData | null)?.description}
      isLoading={isLoading}
      error={getErrorMessage()}
    >
      {currentCommunity && (
        <>
          <CommunityImages community={currentCommunity as unknown as CommunityData} />

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Overview" />
              <Tab label="Members" />
              <Tab label="Vendors" />
              <Tab label="Info" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <CommunityInfo community={currentCommunity as unknown as CommunityData} />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <CommunityMembers community={currentCommunity as unknown as CommunityData} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <CommunityVendors community={currentCommunity as unknown as CommunityData} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Coming Soon
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Community info enhancements coming soon...
              </Typography>
            </Box>
          </TabPanel>

          {canEdit && (
            <Fab
              color="primary"
              aria-label="edit"
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
              }}
              onClick={handleEdit}
            >
              <EditIcon />
            </Fab>
          )}
        </>
      )}
    </EditPageLayout>
  );
};

export default CommunityPage;
